import { Button, DatePicker, Form } from "antd";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import LockIcon from "../../../assets/icons/LockIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import TopCard from "../../../components/common/TopCard";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import { dateFormat, timeFormat } from "../../../constants/constants";
import {
  useGetDeviceListQuery,
  useStopEnrollmentDeviceMutation,
} from "../../../features/device/deviceApi";
import {
  useSyncCheckInMutation,
  useSyncCheckOutMutation,
} from "../../../features/employee/employeeApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import { GetDeviceListType } from "../../../types";
import { capitalize } from "../../../utils/string";
import { addKeyInArray } from "../../../utils/utils";

enum Status {
  active = "status-success",
  inactive = "status-danger",
}

export default function DevicesList() {
  const [form] = Form.useForm();
  const location = useLocation();
  const { open, handleClose, handleOpen } = useModal<string, "CONFIRM">();
  // const { page, limit, handlePagination, handleLimit } = useTable();
  const { isLoading, isError, error, data } = useGetDeviceListQuery({});
  const selectedDate = Form.useWatch("date", form);
  const [
    stopEnrollmentDevice,
    {
      isError: isStopEnDeviceError,
      error: stopEnDeviceError,
      isLoading: isStopEnDeviceLoading,
      isSuccess: isStopEnDeviceSuccess,
    },
  ] = useStopEnrollmentDeviceMutation();
  const [
    checkIn,
    {
      isLoading: checkInning,
      isSuccess: checkInned,
      isError: isCheckInError,
      error: checkInError,
    },
  ] = useSyncCheckInMutation();
  const [
    checkOut,
    {
      isLoading: checkOuting,
      isSuccess: checkedOut,
      isError: isCheckoutError,
      error: checkoutError,
    },
  ] = useSyncCheckOutMutation();

  const handleFinish = (values: any) => {
    const formattedDate = values?.date?.format("YYYY-MM-DD");

    if (values.type === "check_in") {
      checkIn({ date: formattedDate });
    } else if (values.type === "check_out") {
      checkOut({ date: formattedDate });
    }
  };

  useError(isError, error, "Get device list fail");
  useError(isStopEnDeviceError, stopEnDeviceError, "Enrollment stopped fail");
  useError(isCheckInError, checkInError, "Sync Check In failed");
  useError(isCheckoutError, checkoutError, "Sync Check Out failed");

  const columns: ColumnsType<GetDeviceListType["data"][0]> = [
    {
      title: "#SL",
      dataIndex: "index",
      align: "center",
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Identifier",
      dataIndex: "identifier",
    },
    {
      title: "Phone",
      dataIndex: "phone_number",
      render: (val) => val || "-",
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (val) => val || "-",
    },
    {
      title: "Location",
      dataIndex: "location",
      render: (val) => val || "-",
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (val) => val || "-",
    },
    {
      title: "Last update",
      dataIndex: "last_communication_at",
      render: (val) =>
        val ? moment(val).format(`${dateFormat} ${timeFormat}`) : "-",
    },
    {
      title: "Status",
      align: "center",
      dataIndex: "status",
      render: (val: keyof typeof Status) => {
        return <span className={Status[val]}>{capitalize(val)}</span>;
      },
    },
    {
      title: "Action",
      align: "center",
      dataIndex: "identifier",
      render: (val) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            className="action-button"
            size="small"
            onClick={() => handleOpen({ type: "CONFIRM", data: val })}
            icon={<LockIcon />}
          />

          <Link to={`${location.pathname}/view/${val}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (isStopEnDeviceSuccess) {
      toast("success", "Enrollment stopped successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStopEnDeviceSuccess]);
  useEffect(() => {
    if (checkInned) {
      toast("success", "Sync Check In successful");
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInned]);

  useEffect(() => {
    if (checkedOut) {
      toast("success", "Sync Check Out successful");
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedOut]);

  return (
    <>
      <SidebarLayout title="Devices List">
        {open.type === "CONFIRM" && (
          <ConfirmModal
            handleOk={() =>
              stopEnrollmentDevice({
                id: Number(open?.data),
              })
            }
            confirmTitle={`Enrollment Stopped`}
            questionText={`Are you sure you want to stop enrollment?`}
            open={open.open}
            handleClose={handleClose}
            okLoading={isStopEnDeviceLoading}
            okText="Confirm"
          />
        )}
        <Form form={form} onFinish={handleFinish} layout="vertical">
          <TopCard
            className="mb-4"
            leftChildren={
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: "Please select a date",
                  },
                ]}
                name="date"
                className="mb-0"
              >
                <DatePicker
                  disabledDate={(current) => {
                    // Disable future dates
                    return current && current > moment().endOf("day");
                  }}
                />
              </Form.Item>
            }
          >
            <Form.Item name="type" hidden>
              <input />
            </Form.Item>
            <Button
              loading={checkInning}
              onClick={() => form.setFieldValue("type", "check_in")}
              htmlType="submit"
              type="primary"
              className="sm-button !px-3"
            >
              Sync Check In
            </Button>
            <Button
              loading={checkOuting}
              onClick={() => form.setFieldValue("type", "check_out")}
              htmlType="submit"
              type="primary"
              className="sm-button !px-3"
            >
              Sync Check Out
            </Button>
          </TopCard>
        </Form>
        <CustomTable
          loading={isLoading}
          columns={columns}
          dataSource={addKeyInArray(data?.data)}
          // limit={limit}
          // page={page}
          // handleLimit={handleLimit}
          // handlePagination={handlePagination}
          // total={100}
        />
      </SidebarLayout>
    </>
  );
}
